import { ChangeDetectionStrategy, Component, computed, effect, inject, input, signal } from '@angular/core';
import { DatePipe } from '@angular/common';
import { map, of, switchMap } from 'rxjs';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MeasurementsService, StagesService } from '@cosmos/angular/api';
import { ErrorService, MeasurementRefreshService, RBACService, StateService } from '@cosmos/angular/utils';
import { Device, Measurement, Project, User } from '@cosmos/shared';
import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { DialogService, DynamicDialogModule } from 'primeng/dynamicdialog';
import { MeasurementRescheduleForm, EditMeasurementForm } from '../../dynamic-form/forms/measurement.form';
import { DynamicFormDialog } from '../../dynamic-form/dialog-form/dialog-form.component';
import { MeasurementChangeStageDialogComponent } from '../../components/measurement-change-stage-dialog/measurement-change-stage-dialog.component';
import { MeasurementInfoCardComponent } from '../../components/measurement-info-card/measurement-info-card.component';
import { BreadcumbsService } from '../../navigation/breadcrumbs/breadcrumbs.service';
import { MonitoringTreeService } from '../../monitoring/monitoring-tree.service';

@Component({
  selector: 'cosmos-measurement-never-started',
  templateUrl: './measurement-never-started.component.html',
  imports: [TranslatePipe, DatePipe, CardModule, ButtonModule, DynamicDialogModule, MeasurementInfoCardComponent],
  providers: [DialogService, TranslateService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MeasurementNeverStartedComponent {
  private readonly measurementsService = inject(MeasurementsService);
  private readonly stagesService = inject(StagesService);
  private readonly errorService = inject(ErrorService);
  private readonly translateService = inject(TranslateService);
  private readonly dialogService = inject(DialogService);
  private readonly stateService = inject(StateService);
  private readonly refreshService = inject(MeasurementRefreshService);
  private readonly breadcumbsService = inject(BreadcumbsService);
  private readonly treeService = inject(MonitoringTreeService);
  readonly rbac = inject(RBACService);

  readonly measurementId = input<string>();

  readonly measurement = signal<Measurement | null>(null);
  readonly device = signal<Device | null>(null);
  readonly project = signal<Project | null>(null);

  user: User;

  readonly pointCount = computed(() => this.measurement()?.timeAll?.length ?? 0);
  readonly firstTimestamp = computed(() => this.measurement()?.timeAll?.[0] ?? null);
  readonly lastTimestamp = computed(() => {
    const t = this.measurement()?.timeAll;
    return t?.length ? t[t.length - 1] : null;
  });

  constructor() {
    effect(() => {
      if (this.measurementId()) {
        this.loadMeasurement();
      }
    });

    this.stateService.currentUser$.subscribe((x) => { this.user = x; });
  }

  loadMeasurement(): void {
    this.measurementsService.findById(this.measurementId(), '?populate_project&populate_device=name').subscribe({
      next: (response) => {
        this.project.set(response.body.project as Project);
        this.measurement.set(response.body);
      },
      error: (e) => this.errorService.handleError(e),
      complete: () => {
        this.device.set(this.measurement().device as Device || null);
      },
    });
  }

  editMeasurement(): void {
    const m = this.measurement();
    const editMeasurementForm = new EditMeasurementForm(this.translateService, m, this.user, {
      visualizationConfig: this.project()?.visualizationConfig, measurementType: m.measurementType,
    });
    const dialogRef = this.dialogService.open(DynamicFormDialog, {
      data: { form: editMeasurementForm },
      modal: true, closable: true,
      header: this.translateService.instant('dialog.title.edit.measurement'),
      width: '50vw',
      breakpoints: { '960px': '75vw', '640px': '90vw' },
    });

    dialogRef.onClose.subscribe((result) => {
      if (result !== undefined) {
        this.measurementsService.update([m._id, result]).subscribe(
          this.errorService.handleMutation({
            successMessageKey: 'measurement.messages.updateSuccess',
            onSuccess: () => this.loadMeasurement(),
          }),
        );
      }
    });
  }

  moveStage(): void {
    const m = this.measurement();
    const projectId = this.project()?._id;
    this.fetchProjectStageTree(projectId).subscribe({
      next: (stageTree) => {
        const dialogRef = this.dialogService.open(MeasurementChangeStageDialogComponent, {
          data: { measurement: m, stageTree },
          header: this.translateService.instant('measurement.move_stage.title'),
          modal: true, closable: true, width: '30vw',
          breakpoints: { '960px': '75vw', '640px': '90vw' },
        });
        dialogRef.onClose.subscribe((result: { stage: string } | undefined) => {
          if (result !== undefined) {
            this.measurementsService.changeStage(m._id, result).subscribe(
              this.errorService.handleMutation({
                successMessageKey: 'measurement.messages.updateSuccess',
                onSuccess: () => {
                  // The measurement's URL no longer encodes its stage (flat
                  // /monitoring/measurement/:id route), so a stage-only move
                  // doesn't change the URL — no navigate needed.
                  this.refreshService.trigger();
                  // The measurement moved to a different stage — the tree rail
                  // must re-parent its leaf.
                  this.treeService.refresh();
                  // Breadcrumbs are only re-resolved on real navigation; a
                  // stage-only move doesn't change the URL, so nothing else
                  // would refresh the (now stale) breadcrumb trail.
                  this.breadcumbsService.refresh();
                  // Keeps this component's own `measurement` signal in sync,
                  // so re-opening the dialog shows the new stage, not the old one.
                  this.loadMeasurement();
                },
              }),
            );
          }
        });
      },
      error: (e) => this.errorService.handleError(e),
    });
  }

  // Fetches every root stage in the project, then each root's full
  // descendant tree — the shape the change-stage dialog's tree picker needs.
  private fetchProjectStageTree(projectId: string) {
    return this.stagesService.find(`?project=${projectId}&rootOnly=true`).pipe(
      switchMap((rootsRes) => {
        const roots = (rootsRes.body as any[]) ?? [];
        if (roots.length === 0) return of([]);
        return this.stagesService.getForest(roots.map((r) => r._id)).pipe(map((res) => res.body ?? []));
      }),
    );
  }

  openRescheduleDialog(): void {
    const m = this.measurement();
    if (!m?.timeAll?.length) {
      this.errorService.notify('warn', this.translateService.instant('measurement.reschedule.no-data'));
      return;
    }

    const rescheduleForm = new MeasurementRescheduleForm(this.translateService, m.timeAll, 0, m.timeAll.length - 1, this.user, {});
    const dialogRef = this.dialogService.open(DynamicFormDialog, {
      data: { form: rescheduleForm },
      header: this.translateService.instant('dialog.title.reschedule'),
      modal: true, closable: true, width: '50vw',
      breakpoints: { '960px': '75vw', '640px': '90vw' },
    });

    dialogRef.onClose.subscribe((result) => {
      if (result !== undefined) {
        this.measurementsService.reschedule(this.measurementId(), result.timestampStart, result.timestampEnd).subscribe(
          this.errorService.handleMutation({
            successMessageKey: 'measurement.reschedule.success',
            onSuccess: () => this.refreshService.trigger(),
          }),
        );
      }
    });
  }
}
